import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import type { ChatModelOption } from "../hooks/use-chat-model";
import { getProviderIcon, getProviderLabel } from "../provider-icon";

export type ModelSelectorProps = {
  /** Models offered to the user, in display order. */
  models: ChatModelOption[];
  /** Currently selected model id, or null while none is resolved. */
  selectedModel: string | null;
  /** Called with the chosen model id. */
  onSelect: (id: string) => void;
};

function groupByProvider(
  models: ChatModelOption[]
): Map<string, ChatModelOption[]> {
  const grouped = new Map<string, ChatModelOption[]>();
  for (const option of models) {
    const existing = grouped.get(option.provider);
    if (existing) {
      existing.push(option);
    } else {
      grouped.set(option.provider, [option]);
    }
  }
  return grouped;
}

/**
 * Provider/model picker for the chat composer.
 *
 * - 0 or 1 model: renders nothing (no choice to make).
 * - 1 provider, multiple models: a flat list of models.
 * - 2+ providers: one entry per provider, each opening a submenu of its models.
 */
export function ModelSelector({
  models,
  selectedModel,
  onSelect,
}: ModelSelectorProps) {
  if (models.length <= 1) {
    return null;
  }

  const byProvider = groupByProvider(models);
  const selectedOption = models.find((option) => option.id === selectedModel);
  const ActiveIcon = selectedOption
    ? getProviderIcon(selectedOption.provider)
    : null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        render={
          <Button className="h-9 gap-2 px-3" size="lg" variant="outline" />
        }
      >
        {ActiveIcon && <ActiveIcon className="size-4" />}
        <span className="max-w-40 truncate">
          {selectedOption?.label ?? "Select model"}
        </span>
        <ChevronDown className="size-4 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {byProvider.size > 1 ? (
          [...byProvider].map(([provider, providerModels]) => {
            const ProviderIcon = getProviderIcon(provider);
            return (
              <DropdownMenuSub key={provider}>
                <DropdownMenuSubTrigger className="gap-2">
                  <ProviderIcon className="size-4" />
                  <span>{getProviderLabel(provider)}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-48">
                  {providerModels.map((option) => (
                    <DropdownMenuItem
                      className="gap-2"
                      key={option.id}
                      onClick={() => onSelect(option.id)}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      {option.id === selectedModel && (
                        <Check className="size-4" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          })
        ) : (
          <DropdownMenuRadioGroup
            onValueChange={(value) => onSelect(value)}
            value={selectedModel ?? undefined}
          >
            {models.map((option) => (
              <DropdownMenuRadioItem
                className="gap-2"
                key={option.id}
                value={option.id}
              >
                <span className="truncate font-medium">{option.label}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
